import axios from 'axios';
import { buildCxApiUrl } from '../config/cxApiConfig';

export interface PublicFinixConfig {
  applicationId: string;
  merchantId: string;
  environment: 'sandbox' | 'prod';
}
export interface PublicFinixPayment {
  token: string;
  sessionKey: string;
  selectedAmountCents: number;
  coverProcessingFee: boolean;
  componentId: string;
  idempotencyKey: string;
}
const endpoint = (code: string) => buildCxApiUrl(`/api/cx/v1/public/surveys/${encodeURIComponent(code)}/tips/finix`);
export async function getPublicFinixConfig(code: string) {
  return (await axios.get<PublicFinixConfig>(`${endpoint(code)}/config`)).data;
}
export async function payPublicFinix(code: string, input: PublicFinixPayment) {
  return (await axios.post<{status: 'succeeded' | 'pending'}>(endpoint(code), input)).data;
}

export interface FinixSDK {
  Auth(environment: string, merchant: string): { getSessionKey(): string };
  PaymentForm(element: HTMLElement, environment: string, app: string, options: {
    paymentMethods: string[];
    showAddress: boolean;
    hideFields?: string[];
    requiredFields?: string[];
    onSubmit(error: unknown, response?: {data?: {id?: string}}): void;
  }): { destroy?: () => void };
}
// v1 is loaded by index.html for the legacy payment page. Keep that reference
// available even during v2's initialization; v2 refuses to initialize over v1.
const legacySDK = window.Finix;
export function getLegacyFinixSDK() { return legacySDK; }
let sdkPromise: Promise<FinixSDK> | undefined;
export function loadPublicFinix(): Promise<FinixSDK> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<FinixSDK>((resolve, reject) => {
    const host = window as unknown as {Finix?: FinixSDK};
    const previous = host.Finix;
    if (previous?.PaymentForm && previous?.Auth) { resolve(previous); return; }
    delete host.Finix;
    const restore = () => { host.Finix = previous; };
    const script = document.createElement('script');
    script.src = 'https://js.finix.com/v/2/finix.js';
    script.async = true;
    script.onload = () => {
      const sdk = host.Finix;
      restore();
      if (sdk?.PaymentForm && sdk?.Auth) resolve(sdk);
      else reject(new Error('Payment form unavailable'));
    };
    script.onerror = () => { restore(); script.remove(); reject(new Error('Payment form unavailable')); };
    document.head.appendChild(script);
  }).catch(error => { sdkPromise = undefined; throw error; });
  return sdkPromise;
}
